import React from 'react'
import { useSpring, animated, useInView } from '@react-spring/web'

interface SubVisualProps {
	backgroundImage?: string
	backgroundVideo?: string
	title: string
	subtitle?: string
	height?: string
	overlayOpacity?: number
	children?: React.ReactNode
}

const SubVisual: React.FC<SubVisualProps> = ({
	backgroundImage,
	backgroundVideo,
	title,
	subtitle,
	height = 'h-[75vh]',
	overlayOpacity = 0.5,
	children,
}) => {
	const [refInView, inView] = useInView()

	// 메인 컨테이너 애니메이션
	const containerAnimation = useSpring({
		from: { opacity: 0, transform: 'scale(1.1)' },
		to: inView
			? { opacity: 1, transform: 'scale(1)' }
			: { opacity: 0, transform: 'scale(1.1)' },
		config: { mass: 1, tension: 200, friction: 30 },
	})

	// 텍스트 애니메이션
	const titleAnimation = useSpring({
		from: { opacity: 0, transform: 'translateY(30px)' },
		to: inView
			? { opacity: 1, transform: 'translateY(0px)' }
			: { opacity: 0, transform: 'translateY(30px)' },
		config: { mass: 1, tension: 280, friction: 60 },
		delay: 200,
	})

	const subtitleAnimation = useSpring({
		from: { opacity: 0, transform: 'translateY(20px)' },
		to: inView
			? { opacity: 1, transform: 'translateY(0px)' }
			: { opacity: 0, transform: 'translateY(20px)' },
		config: { mass: 1, tension: 280, friction: 60 },
		delay: 400,
	})

	// 오버레이 애니메이션
	const overlayAnimation = useSpring({
		from: { opacity: 0 },
		to: inView ? { opacity: overlayOpacity } : { opacity: 0 },
		config: { mass: 1, tension: 200, friction: 25 },
		delay: 100,
	})

	// 하단 라인 애니메이션
	const lineAnimation = useSpring({
		from: { transform: 'scaleX(0)' },
		to: inView ? { transform: 'scaleX(1)' } : { transform: 'scaleX(0)' },
		config: { mass: 1, tension: 200, friction: 25 },
		delay: 600,
	})

	return (
		<animated.div
			ref={refInView}
			className={`relative w-full ${height} overflow-hidden`}
			style={containerAnimation}
		>
			{/* 배경 비디오, 이미지 또는 콘텐츠 */}
			{backgroundVideo ? (
				<video
					className="absolute inset-0 w-full h-full object-cover"
					autoPlay
					muted
					loop
					playsInline
				>
					<source
						src={backgroundVideo}
						type="video/mp4"
					/>
				</video>
			) : backgroundImage ? (
				<div
					className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-700"
					style={{
						backgroundImage: `url(${backgroundImage})`,
					}}
				/>
			) : (
				children
			)}

			{/* 오버레이 */}
			<animated.div
				className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70"
				style={overlayAnimation}
			/>

			{/* 텍스트 콘텐츠 */}
			<div className="absolute inset-0 flex items-center justify-center">
				<div className="text-center text-white px-4 max-w-4xl">
					<animated.h1
						className="text-4xl md:text-6xl font-bold mb-4 leading-tight eng-bold"
						style={titleAnimation}
					>
						{title}
					</animated.h1>
					{subtitle && (
						<animated.p
							className="text-lg md:text-xl opacity-90 leading-relaxed"
							style={subtitleAnimation}
						>
							{subtitle}
						</animated.p>
					)}
				</div>
			</div>

			{/* 하단 장식 라인 */}
			<animated.div
				className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent origin-left shadow-lg shadow-orange-500/50"
				style={lineAnimation}
			/>
		</animated.div>
	)
}

export default SubVisual
